import React, { useState, useEffect, useCallback } from 'react';
import { Download, Loader2, Warehouse, CalendarDays, ArrowLeftRight, Search } from 'lucide-react';
import { stockRegistersAPI, branchAPI } from '../../services/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const SUBTABS = [
  { id: 'godown', label: 'Current Stock — Godown-wise', icon: Warehouse },
  { id: 'as-on-date', label: 'Stock as on Date', icon: CalendarDays },
  { id: 'inout', label: 'Inward / Outward', icon: ArrowLeftRight },
];

const fmtQty = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const fmtInr = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const todayYMD = () => new Date().toISOString().slice(0, 10);

const StockRegisters = () => {
  const [sub, setSub] = useState('godown');
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState('');
  const [search, setSearch] = useState('');
  const [asOnDate, setAsOnDate] = useState(todayYMD());
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => { branchAPI.getAll().then(setBranches).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branch) params.branch = branch;
      if (search.trim()) params.search = search.trim();
      let res;
      if (sub === 'godown') {
        res = await stockRegistersAPI.getGodownWise(params);
      } else if (sub === 'as-on-date') {
        res = await stockRegistersAPI.getAsOnDate({ ...params, as_on_date: asOnDate });
      } else {
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        res = await stockRegistersAPI.getInwardOutward(params);
      }
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setData(null);
    } finally { setLoading(false); }
  }, [sub, branch, search, asOnDate, fromDate, toDate]);

  // Auto-load on sub-tab / branch change (search is manual via button/enter)
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sub, branch, asOnDate]);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (sub === 'inout') {
      const aoa = [['Product', 'SKU', 'Category', 'Sub-category', 'Unit', 'Total Inward', 'Total Outward', 'Net']];
      (data.rows || []).forEach(r => aoa.push([r.product_name, r.sku, r.category, r.sub_category, r.unit, r.total_inward, r.total_outward, r.net]));
      aoa.push(['TOTAL', '', '', '', '', data.totals?.total_inward, data.totals?.total_outward, data.totals?.net]);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Inward-Outward');
    } else {
      const isGod = sub === 'godown';
      const aoa = [[ 'Godown', 'Product', 'SKU', 'Unit', ...(isGod ? ['Net Qty', 'Reserved', 'Available'] : ['Qty']), 'Cost', 'Stock Value']];
      (data.godowns || []).forEach(g => {
        g.items.forEach(it => aoa.push([
          g.godown_name, it.product_name, it.sku, it.unit,
          ...(isGod ? [it.net_qty, it.reserved_qty, it.available_qty] : [it.qty]),
          it.cost_price, it.stock_value,
        ]));
        aoa.push([`${g.godown_name} — SUBTOTAL`, '', '', '', ...(isGod ? [g.total_qty, '', ''] : [g.total_qty]), '', g.total_value]);
      });
      aoa.push(['GRAND TOTAL', '', '', '', ...(isGod ? [data.grand_total_qty, '', ''] : [data.grand_total_qty]), '', data.grand_total_value]);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), sub === 'godown' ? 'Godown-wise' : 'As-on-Date');
    }
    XLSX.writeFile(wb, `stock-${sub}-${todayYMD()}.xlsx`);
    toast.success('Excel exported');
  };

  return (
    <div data-testid="stock-registers">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Stock Registers</h2>
          <p className="text-xs text-gray-500">Godown-wise stock, stock as on any date, and inward/outward movement</p>
        </div>
        <button onClick={exportExcel} disabled={!data || loading} data-testid="stock-reg-export"
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-40" style={{ backgroundColor: '#24549c' }}>
          <Download className="w-3.5 h-3.5" /> Export Excel
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {SUBTABS.map(t => (
          <button key={t.id} onClick={() => setSub(t.id)} data-testid={`stock-reg-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-lg border transition-all ${
              sub === t.id ? 'text-white border-transparent' : 'text-gray-600 bg-white border-gray-200 hover:border-blue-300'}`}
            style={sub === t.id ? { backgroundColor: '#24549c' } : {}}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-white p-3 rounded-xl border border-gray-100">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Godown</label>
          <select value={branch} onChange={e => setBranch(e.target.value)} data-testid="stock-reg-godown-filter"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg min-w-[180px]">
            <option value="">All Godowns</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        {sub === 'as-on-date' && (
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">As on Date</label>
            <input type="date" value={asOnDate} onChange={e => setAsOnDate(e.target.value)} data-testid="stock-reg-ason-date"
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
          </div>
        )}

        {sub === 'inout' && (
          <>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">From</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid="stock-reg-from-date"
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">To</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} data-testid="stock-reg-to-date"
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
            </div>
          </>
        )}

        <div className="flex-1 min-w-[180px]">
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Search product / SKU / category</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
              placeholder="Type and press Enter…" data-testid="stock-reg-search"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
          </div>
        </div>

        <button onClick={load} data-testid="stock-reg-apply"
          className="px-4 py-1.5 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
          Apply
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="stock-reg-loading"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#24549c' }} /></div>
      ) : sub === 'inout' ? (
        <InwardOutwardTable data={data} />
      ) : (
        <GodownGrouped data={data} isGodown={sub === 'godown'} />
      )}
    </div>
  );
};

const KpiBar = ({ items }) => (
  <div className="flex flex-wrap gap-3 mb-4">
    {items.map((k, i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-100 px-4 py-3" data-testid={`stock-reg-kpi-${i}`}>
        <p className="text-[10px] text-gray-500 font-semibold uppercase">{k.l}</p>
        <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{k.v}</p>
      </div>
    ))}
  </div>
);

const GodownGrouped = ({ data, isGodown }) => {
  if (!data || !data.godowns?.length) return <Empty />;
  return (
    <div>
      <KpiBar items={[
        { l: 'Godowns', v: data.godown_count },
        { l: 'Products', v: data.product_count },
        { l: 'Total Qty', v: fmtQty(data.grand_total_qty) },
        { l: 'Total Stock Value', v: fmtInr(data.grand_total_value) },
        ...(data.as_on_date ? [{ l: 'As On', v: data.as_on_date }] : []),
      ]} />
      {data.godowns.map((g, gi) => (
        <div key={gi} className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-4" data-testid={`stock-reg-godown-${gi}`}>
          <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-blue-100">
            <span className="text-sm font-bold text-gray-900 flex items-center gap-2"><Warehouse className="w-4 h-4" style={{ color: '#24549c' }} />{g.godown_name}</span>
            <span className="text-[11px] text-gray-600">{g.item_count} items · Qty {fmtQty(g.total_qty)} · <span className="font-semibold text-gray-900">{fmtInr(g.total_value)}</span></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-gray-100 bg-gray-50">
                {['Product', 'SKU', 'Unit', ...(isGodown ? ['Net Qty', 'Reserved', 'Available'] : ['Qty']), 'Cost', 'Stock Value'].map(h =>
                  <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody>
                {g.items.map((it, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-2 font-semibold text-gray-900">{it.product_name}</td>
                    <td className="px-4 py-2 text-gray-400">{it.sku || '—'}</td>
                    <td className="px-4 py-2 text-gray-400">{it.unit}</td>
                    {isGodown ? (<>
                      <td className="px-4 py-2 font-semibold text-gray-900">{fmtQty(it.net_qty)}</td>
                      <td className="px-4 py-2 text-amber-600">{fmtQty(it.reserved_qty)}</td>
                      <td className="px-4 py-2 text-green-700">{fmtQty(it.available_qty)}</td>
                    </>) : (
                      <td className="px-4 py-2 font-semibold text-gray-900">{fmtQty(it.qty)}</td>
                    )}
                    <td className="px-4 py-2 text-gray-500">{fmtInr(it.cost_price)}</td>
                    <td className="px-4 py-2 font-semibold text-gray-900">{fmtInr(it.stock_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

const InwardOutwardTable = ({ data }) => {
  if (!data || !data.rows?.length) return <Empty />;
  return (
    <div>
      <KpiBar items={[
        { l: 'Products', v: data.product_count },
        { l: 'Total Inward', v: fmtQty(data.totals?.total_inward) },
        { l: 'Total Outward', v: fmtQty(data.totals?.total_outward) },
        { l: 'Net Movement', v: fmtQty(data.totals?.net) },
      ]} />
      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-xs" data-testid="stock-reg-inout-table">
          <thead><tr className="border-b border-gray-100 bg-gray-50">
            {['Product', 'SKU', 'Category', 'Unit', 'Total Inward', 'Total Outward', 'Net'].map(h =>
              <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="px-4 py-2 font-semibold text-gray-900">{r.product_name}</td>
                <td className="px-4 py-2 text-gray-400">{r.sku || '—'}</td>
                <td className="px-4 py-2 text-gray-400">{r.sub_category || r.category || '—'}</td>
                <td className="px-4 py-2 text-gray-400">{r.unit}</td>
                <td className="px-4 py-2 text-green-700 font-semibold">{fmtQty(r.total_inward)}</td>
                <td className="px-4 py-2 text-red-600 font-semibold">{fmtQty(r.total_outward)}</td>
                <td className={`px-4 py-2 font-semibold ${r.net < 0 ? 'text-red-600' : 'text-gray-900'}`}>{fmtQty(r.net)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-bold">
              <td className="px-4 py-2.5 uppercase text-[10px] text-gray-600" colSpan={4}>Total</td>
              <td className="px-4 py-2.5 text-green-700">{fmtQty(data.totals?.total_inward)}</td>
              <td className="px-4 py-2.5 text-red-600">{fmtQty(data.totals?.total_outward)}</td>
              <td className="px-4 py-2.5 text-gray-900">{fmtQty(data.totals?.net)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Empty = () => (
  <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-sm text-gray-400" data-testid="stock-reg-empty">
    No stock data for the selected filters
  </div>
);

export default StockRegisters;
